import { t } from '@app/i18n';
import { Icon } from '@zarya/theme';
import { AskTrail } from './AskTrail';
import type { ConversationWindow } from './ChatEntryModel';
import { askOutcome, noAnswerPhrase, type AgentAskResult } from './ChatModel';
import { useTypewriter } from './useTypewriter';
import type { ChatMessage as Message } from './useChat';
import s from './Chat.module.css';

export interface ChatMessageProps {
  message: Message;
  /** Milissegundos por letra da resposta; `0` mostra tudo de uma vez. */
  msPerChar?: number;
}

/** Uma linha da transcrição: a pergunta da pessoa ou a resposta do agente. */
export function ChatMessage({ message, msPerChar }: ChatMessageProps) {
  if (message.role === 'person') {
    return (
      <article className={`${s.message} ${s.messagePerson}`} aria-label={t('chat.message.asked', { who: message.who })}>
        <span className={`${s.avatar} ${s.avatarPerson}`} aria-hidden="true">
          <Icon name="user" size="sm" />
        </span>
        <div className={`${s.bubble} ${s.bubblePerson}`}>
          <span className={s.who}>{message.who}</span>
          <p className={s.text}>{message.text}</p>
        </div>
      </article>
    );
  }
  const conversation: ConversationWindow | undefined = message.finishedAt
    ? {
        agent: message.who,
        sub: message.sub,
        startedAt: new Date(message.startedAt),
        finishedAt: new Date(message.finishedAt),
        // The id the console answered with; it is the one the screen sent.
        conversationId: message.result?.conversation_id ?? message.conversationId,
      }
    : undefined;
  return (
    <AgentMessage who={message.who} result={message.result} msPerChar={msPerChar} conversation={conversation} />
  );
}

function AgentMessage({
  who,
  result,
  msPerChar,
  conversation,
}: {
  who: string;
  result: AgentAskResult | null;
  msPerChar?: number;
  conversation?: ConversationWindow;
}) {
  const outcome = result ? askOutcome(result) : null;
  const typed = useTypewriter(result?.answer ?? null, { msPerChar });

  return (
    <article className={`${s.message} ${s.messageAgent}`} aria-label={t('chat.message.answered', { who })}>
      <span className={`${s.avatar} ${s.avatarAgent}`} aria-hidden="true">
        <Icon name="robot" size="sm" />
      </span>
      <div
        className={[
          s.bubble,
          s.bubbleAgent,
          !result ? s.bubblePending : '',
          outcome === 'llm_disabled' ? s.bubbleDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
        // O clique conclui a digitação (§4). Um `button` em volta do texto
        // inteiro seria pesado para o leitor de tela; a bolha é clicável, o
        // teclado tem o botão «{t('chat.message.showAll')}» abaixo, e o texto inteiro já está
        // no DOM acessível (`aria-label` do parágrafo).
        onClick={typed.done ? undefined : typed.finish}
        onKeyDown={
          typed.done
            ? undefined
            : (event) => {
                if (event.key === 'Enter' || event.key === ' ') typed.finish();
              }
        }
        data-typing={!typed.done ? '' : undefined}
        data-testid="chat-answer"
      >
        <span className={s.who}>{who}</span>

        {!result ? (
          <p className={`${s.text} ${s.thinking}`} role="status">
            {t('chat.message.thinking')}
            <span className={s.dots} aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </p>
        ) : null}

        {result && outcome === 'answered' ? (
          <>
            <p className={s.text} aria-live="polite" aria-label={result.answer ?? undefined}>
              <span aria-hidden="true">{typed.shown}</span>
              {!typed.done ? (
                <span className={s.caret} aria-hidden="true">
                  ▍
                </span>
              ) : null}
            </p>
            {!typed.done ? (
              <button type="button" className={s.finish} onClick={typed.finish}>
                mostrar tudo
              </button>
            ) : null}
          </>
        ) : null}

        {result && outcome === 'llm_disabled' ? (
          <div className={s.disabled} role="note">
            <p className={s.text}>
              {/* O slug do agente é dado, não texto: entra como parâmetro da
                  frase em vez de fatiar a sentença com `<code>` no meio — em
                  inglês ele não fica na mesma posição. */}
              <strong>{t('chat.message.withoutModelKey', { agent: result.agent })}</strong>
              {t('chat.message.nothingCalled')}
            </p>
          </div>
        ) : null}

        {result && outcome === 'no_answer' ? <p className={s.text}>{noAnswerPhrase(result)}</p> : null}

        {result ? <AskTrail result={result} conversation={conversation} /> : null}
      </div>
    </article>
  );
}
